import type { FeedbackBundle, FeedbackMatch } from "@/lib/types";

/**
 * Player-facing feedback IA.
 *
 * An action-first tab set (`overview · review · goals · history`) that
 * mirrors the questions
 * a player or guardian actually brings to the page:
 *   - Overview — what should I do next?
 *   - Review   — what is there to review?
 *   - Goals    — what am I working on?
 *   - History  — what have I already covered?
 *
 * The router accepts BOTH the new and the legacy `?tab=` values; the
 * shell normalizes legacy values to the right action tab without
 * rewriting the URL. Deep links shared by coaches before this redesign
 * (and stale bookmarks) keep resolving to the right place.
 */

/** The four action-oriented player-facing tabs. */
export type FeedbackTab = "overview" | "review" | "goals" | "history";

/** Every `?tab=` value the `/feedback` shell accepts — the four new
 * tabs PLUS the five legacy object-type values. The router's Zod
 * schema enumerates this union; the shell normalizes via
 * `normalizeFeedbackTab` before render. */
export const FEEDBACK_TAB_PARAM_VALUES = [
  "overview",
  "review",
  "goals",
  "history",
  // Legacy aliases — preserved so existing deep links never hit the
  // parse-error boundary.
  "playlists",
  "notes",
  "clips",
  "summaries",
  "development",
] as const;

export type FeedbackTabParamValue = (typeof FEEDBACK_TAB_PARAM_VALUES)[number];

/** Map any inbound `?tab=` value (or `undefined`) onto one of the four
 * action tabs. Legacy object-tab values fold into the action tab that
 * now hosts that material:
 *
 *   - `playlists` / `notes` / `clips` / `summaries` → `review`
 *   - `development` → `goals`
 *   - unknown / missing → `overview`
 *
 * The shell calls this once per render; it does NOT rewrite the URL,
 * so back-button history and shared links stay stable.
 */
export function normalizeFeedbackTab(value?: string | null): FeedbackTab {
  switch (value) {
    case "overview":
    case "review":
    case "goals":
    case "history":
      return value;
    case "playlists":
    case "notes":
    case "clips":
    case "summaries":
      return "review";
    case "development":
      return "goals";
    default:
      return "overview";
  }
}

/** Friendly labels for the action tabs — used by the tab strip and as
 * the accessible name on the active panel. */
export const FEEDBACK_TAB_LABELS: Record<FeedbackTab, string> = {
  overview: "Overview",
  review: "Review",
  goals: "Goals",
  history: "History",
};

/** Resolve a friendly match label from a bundle's `matches[]`. Prefer
 * the server-built `match.label`; fall back to the raw `match_id` so
 * the caller never templates `undefined`. Returns `undefined` only
 * when the caller passed an empty id (so the caller can drop the
 * "vs X" meta entirely).
 *
 * This helper is shared across Overview, Review (PR 2), History (PR 5)
 * and Goals (PR 3) so every player-facing surface uses the same
 * friendly label rather than the raw slug. */
export function matchLabelFor(
  matchesById: Map<string, FeedbackMatch>,
  matchId: string | null | undefined,
): string | undefined {
  if (!matchId) return undefined;
  const match = matchesById.get(matchId);
  const label = match?.label?.trim();
  if (label) return label;
  return matchId;
}

/** Build the `{ matchId → FeedbackMatch }` index a viewer surface needs.
 * The bundle's `matches[]` is optional (older clients), so this safely
 * accepts `undefined`. */
export function matchesById(
  bundle: FeedbackBundle | undefined,
): Map<string, FeedbackMatch> {
  const out = new Map<string, FeedbackMatch>();
  for (const m of bundle?.matches ?? []) {
    out.set(m.id, m);
  }
  return out;
}
